import * as React from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

type CommonProps<TOption> = {
    label: string;
    options: TOption[];
    getOptionLabel: (option: TOption) => string;
    isOptionEqualToValue: (option: TOption, value: TOption) => boolean;

    loading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    error?: boolean;
    helperText?: React.ReactNode;
};

// Single-select props
type SingleProps<TOption> = CommonProps<TOption> & {
    multiple?: false;
    value: TOption | null;
    onChange: (value: TOption | null) => void;
};

// Multi-select props
type MultiProps<TOption> = CommonProps<TOption> & {
    multiple: true;
    value: TOption[];
    onChange: (value: TOption[]) => void;
};

type Props<TOption> = SingleProps<TOption> | MultiProps<TOption>;

export default function LookupAutocomplete<TOption>(props: Props<TOption>) {
    const {
        label,
        options,
        getOptionLabel,
        isOptionEqualToValue,
        loading = false,
        disabled = false,
        placeholder,
        error,
        helperText,
    } = props;

    const multiple = (props as MultiProps<TOption>).multiple === true;

    return (
        <Autocomplete
            multiple={multiple}
            options={options}
            value={props.value as any}
            onChange={(_, newValue) => props.onChange(newValue as any)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            loading={loading}
            disabled={disabled}
            filterSelectedOptions={multiple}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
